// Firebase Storage Service
import {
  ref,
  uploadBytes,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
  listAll,
  getMetadata,
  updateMetadata,
} from "firebase/storage";
import { storage } from "./config";
import { logger } from "../services/logger.service";

// Upload file to Firebase Storage
export const uploadFile = async (
  file: File,
  path: string,
  onProgress?: (progress: number) => void,
): Promise<string> => {
  try {
    const storageRef = ref(storage, path);

    if (onProgress) {
      // Use resumable upload with progress tracking
      const uploadTask = uploadBytesResumable(storageRef, file);

      return new Promise((resolve, reject) => {
        uploadTask.on(
          "state_changed",
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            onProgress(progress);
          },
          async (error) => {
            await logger.error("storage_upload_failed", error, { file_path: path, file_size: file.size });
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              resolve(downloadURL);
            } catch (error) {
              reject(error);
            }
          },
        );
      });
    } else {
      // Simple upload without progress tracking
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    }
  } catch (error) {
    await logger.error("storage_upload_failed", error as Error, { file_path: path, file_size: file.size });
    throw error;
  }
};

// Get download URL for a file
export const getFileURL = async (path: string): Promise<string> => {
  try {
    const storageRef = ref(storage, path);
    return await getDownloadURL(storageRef);
  } catch (error) {
    console.error("Error getting file URL:", error);
    throw error;
  }
};

// Delete file from Firebase Storage
export const deleteFile = async (path: string): Promise<void> => {
  try {
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
  } catch (error) {
    console.error("Error deleting file:", error);
    throw error;
  }
};

// List all files in a directory
export const listFiles = async (path: string = "") => {
  try {
    const storageRef = ref(storage, path);
    const result = await listAll(storageRef);

    const files = await Promise.all(
      result.items.map(async (itemRef) => {
        const url = await getDownloadURL(itemRef);
        const metadata = await getMetadata(itemRef);
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url,
          size: metadata.size,
          contentType: metadata.contentType,
          timeCreated: metadata.timeCreated,
          updated: metadata.updated,
        };
      }),
    );

    return files;
  } catch (error) {
    console.error("Error listing files:", error);
    throw error;
  }
};

// Get file metadata
export const getFileMetadata = async (path: string) => {
  try {
    const storageRef = ref(storage, path);
    return await getMetadata(storageRef);
  } catch (error) {
    console.error("Error getting file metadata:", error);
    throw error;
  }
};

// Update file metadata
export const updateFileMetadata = async (
  path: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  metadata: { [key: string]: any },
) => {
  try {
    const storageRef = ref(storage, path);
    return await updateMetadata(storageRef, metadata);
  } catch (error) {
    console.error("Error updating file metadata:", error);
    throw error;
  }
};

// Upload multiple files
export const uploadMultipleFiles = async (
  files: File[],
  basePath: string,
  onProgress?: (fileIndex: number, progress: number) => void,
): Promise<string[]> => {
  try {
    const uploadPromises = files.map((file, index) => {
      const path = `${basePath}/${file.name}`;
      const progressCallback = onProgress
        ? (progress: number) => onProgress(index, progress)
        : undefined;

      return uploadFile(file, path, progressCallback);
    });

    return await Promise.all(uploadPromises);
  } catch (error) {
    console.error("Error uploading multiple files:", error);
    throw error;
  }
};

// Load multiple images from storage paths
export const loadMultipleImages = async (
  imagePaths: { id: string; path: string }[],
): Promise<Record<string, string>> => {
  try {
    const imageLoadPromises: Promise<void>[] = [];
    const images: Record<string, string> = {};

    imagePaths.forEach(({ id, path }) => {
      const promise = async () => {
        try {
          const imageRef = ref(storage, path);
          const imageUrl = await getDownloadURL(imageRef);
          images[id] = imageUrl;
        } catch (error) {
          console.error(`Error loading image for ${id}:`, error);
        }
      };
      imageLoadPromises.push(promise());
    });

    await Promise.all(imageLoadPromises);
    return images;
  } catch (error) {
    console.error("Error loading multiple images:", error);
    throw error;
  }
};

// Get image URL from storage path
export const getImageFromPath = async (path: string): Promise<string | null> => {
  try {
    const imageRef = ref(storage, path);
    return await getDownloadURL(imageRef);
  } catch (error) {
    console.error(`Error loading image from path ${path}:`, error);
    return null;
  }
};
